import React, { useEffect } from 'react';
import Head from 'next/head';
import { GetServerSideProps, NextPageWithLayout } from 'next';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import useUser from '@hooks/user/use-user';
import { isWebview } from '@helpers/routing';
import extractFromContext from '@helpers/utils/extract-from-context';
import ConditionalWrapper from '@components/common/conditional-wrapper/conditional-wrapper';
import Button from '@components/atoms/button/button';
import useWindowSize from '@hooks/use-window-size';
import AuthenticationGuard from '@components/atoms/authentication/authentication-guard/authentication-guard';
import { scrollToTop } from '@helpers/utils/general';
import SellerInformationForm from '@components/seller-information/seller-information-form/seller-information-form';
import sanitizer from '@helpers/sanitizer';
import { SellerInformationProvider } from '@context/seller-information.context';
import { getAppInitialProps } from '@helpers/initial-props';
import styles from './index.module.scss';

const DefaultLayout = dynamic(() => import('@layouts/default-layout'));

const SellerInformationPage: NextPageWithLayout = () => {
  const { query } = useRouter();
  const { t } = useTranslation('get-paid');
  const isWebView = isWebview(query);
  const { isAuthenticated } = useUser();
  const { isWiderThanMd } = useWindowSize();

  useEffect(() => {
    if (!isAuthenticated) {
      scrollToTop();
    }
  }, [isAuthenticated]);

  return (
    <>
      <Head>
        <title>{t('GET_PAID.TAX_INFORMATION_FORM.TITLE', 'Your tax information')}</title>
        <meta
          property="og:title"
          content={t('GET_PAID.TAX_INFORMATION_FORM.TITLE', 'Your tax information')}
          key="og:title"
        />
      </Head>
      <ConditionalWrapper
        condition={!isWebView}
        wrap={(children) => <DefaultLayout>{children}</DefaultLayout>}
      >
        <div className={styles.sellerInformation}>
          {!isAuthenticated ? (
            <div className={styles.sellerInformation__pageText}>
              <h2 className={isWiderThanMd ? 'vc-heading-A40' : 'vc-heading-A32'}>
                {t('GET_PAID.TAX_INFORMATION_FORM.TITLE', 'Your tax information')}
              </h2>
              <p
                className={isWiderThanMd ? 'vc-text-H16' : 'vc-text-H14'}
                dangerouslySetInnerHTML={{
                  __html: sanitizer(
                    t(
                      'GET_PAID.TAX_INFORMATION_FORM.DESCRIPTION.GENERIC',
                      'To comply with tax regulations, please provide your tax information before December 31 (US) or January 1 (EU).',
                    ),
                  ),
                }}
              />
              <>
                <p className={isWiderThanMd ? 'vc-text-H16' : 'vc-text-H14'}>
                  {t(
                    'GET_PAID.TAX_INFORMATION_SIGNUP.DESCRIPTION',
                    'To complete your tax information, first log in to your account.',
                  )}
                </p>
                <div className={styles.sellerInformation__pageText__cta}>
                  <AuthenticationGuard>
                    <Button
                      variant="primary"
                      size="large"
                      data-cy="seller-info-login-button"
                    >
                      {t('GET_PAID.TAX_INFORMATION_SIGNUP.CTA', 'Log in')}
                    </Button>
                  </AuthenticationGuard>
                </div>
              </>
            </div>
          ) : (
            <SellerInformationProvider>
              <SellerInformationForm />
            </SellerInformationProvider>
          )}
        </div>
      </ConditionalWrapper>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale } = extractFromContext(context);
  const initialProps = await getAppInitialProps(context);
  const translations = await serverSideTranslations(locale, [
    'default-layout',
    'vc-authentication',
    'get-paid',
    'common',
    'a11y',
  ]);
  return {
    props: {
      ...initialProps,
      ...translations,
    },
  };
};

SellerInformationPage.Layout = [];
SellerInformationPage.PageType = 'SellerInformationPage';

export default SellerInformationPage;
